import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, vModelCheckbox as _vModelCheckbox, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, Transition as _Transition, withCtx as _withCtx, normalizeClass as _normalizeClass, createStaticVNode as _createStaticVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "w-full md:px-6" }
const _hoisted_2 = { class: "mb-8 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" }
const _hoisted_3 = { class: "p-6" }
const _hoisted_4 = { class: "grid grid-cols-1 md:grid-cols-2 gap-8" }
const _hoisted_5 = { class: "space-y-6" }
const _hoisted_6 = { class: "flex items-center gap-4" }
const _hoisted_7 = { class: "relative w-32" }
const _hoisted_8 = ["disabled"]
const _hoisted_9 = { class: "flex items-start gap-3" }
const _hoisted_10 = { class: "flex items-center h-5" }
const _hoisted_11 = ["disabled"]
const _hoisted_12 = { class: "flex flex-wrap gap-4 pt-2" }
const _hoisted_13 = ["disabled"]
const _hoisted_14 = {
  key: 0,
  class: "fas fa-spinner fa-spin mr-2"
}
const _hoisted_15 = {
  key: 1,
  class: "fas fa-check mr-2"
}
const _hoisted_16 = ["disabled"]
const _hoisted_17 = {
  key: 0,
  class: "fas fa-spinner fa-spin mr-2"
}
const _hoisted_18 = {
  key: 1,
  class: "fas fa-trash-alt mr-2"
}
const _hoisted_19 = {
  key: 0,
  class: "p-3 bg-green-50 text-green-700 rounded-md border border-green-100 flex items-center gap-2"
}
const _hoisted_20 = { class: "text-sm font-medium" }
const _hoisted_21 = { class: "text-center py-4" }
const _hoisted_22 = { class: "text-sm text-black mb-4" }
const _hoisted_23 = { class: "flex justify-end space-x-3" }
const _hoisted_24 = { class: "flex justify-end space-x-3" }
const _hoisted_25 = { class: "text-center py-4" }
const _hoisted_26 = { class: "text-lg font-medium text-black mb-2" }
const _hoisted_27 = { class: "text-sm text-black" }
const _hoisted_28 = { class: "flex justify-end space-x-3" }

import { ref, reactive, computed, onMounted, readonly } from 'vue'
import { PageHeader, ModalComp } from '@/components'
import DiscountSection from '@/components/discount/DiscountSection.vue'
import AddDiscountModal from '@/components/discount/AddDiscountModal.vue'
import { getRemises, createRemise, updateRemise, deleteRemise } from '@/api/discounts'
import { applyGlobalPromotion, getProductsPage } from '@/api/produits'
import { Discount, RemiseApi } from '@/types/discount'
import { useAuthStore } from '@/stores/auth'
import {useRouter} from "vue-router";
import { useNotification } from '@/composables/useNotification'

export default /*@__PURE__*/_defineComponent({
  __name: 'MyDiscountView',
  setup(__props) {

const authStore = useAuthStore()
const router = useRouter()
const { success: notifySuccess, error: notifyError } = useNotification()

const globalPromotionPercent = ref<number | null>(0)
const isGlobalPromotionActive = ref(false)
const isApplyingPromotion = ref(false)
const applyToAlreadyDiscounted = ref(false)
const lastAppliedMessage = ref('')

// Logic to determine if global promotion is active on mount
const checkInitialGlobalPromotion = async () => {
  const vendeurId = authStore.user?.id_vendeur
  if (!vendeurId) return

  try {
    // We fetch more products to be more accurate (50 samples)
    const result = await getProductsPage({
      id_vendeur: vendeurId,
      take: 50,
      skip: 0
    })

    if (result.items && result.items.length > 0) {
      const productsWithPromo = result.items.filter(p => p.prixReduit != null && p.prixReduit > 0 && p.prix && p.prix > 0)
      
      if (productsWithPromo.length > 0) {
        // Count occurrences of each percentage to find the most common one (mode)
        const counts: Record<number, number> = {}
        productsWithPromo.forEach(p => {
          if (p.prix && p.prixReduit) {
            const pct = Math.round((1 - p.prixReduit / p.prix) * 100)
            counts[pct] = (counts[pct] || 0) + 1
          }
        })

        let maxCount = 0
        let modePercent = 0
        for (const pct in counts) {
          if (counts[pct] > maxCount) {
            maxCount = counts[pct]
            modePercent = parseInt(pct)
          }
        }

        // If a significant portion of products has this discount, show it
        if (maxCount >= 1) {
          isGlobalPromotionActive.value = true
          globalPromotionPercent.value = modePercent
          lastAppliedMessage.value = `Promotion de ${modePercent}% détectée et appliquée.`
        } else {
          isGlobalPromotionActive.value = false
          globalPromotionPercent.value = null
        }
      } else {
        isGlobalPromotionActive.value = false
        globalPromotionPercent.value = null
      }
    } else {
      isGlobalPromotionActive.value = false
      globalPromotionPercent.value = null
    }
  } catch (err) {
    console.error('Error checking global promotion state:', err)
  }
}

const applyGlobalPromotionAction = async () => {
  if (globalPromotionPercent.value === null) return

  if (globalPromotionPercent.value < 0) {
    notifyError('La promotion ne peut pas être négative.')
    return
  }

  if (globalPromotionPercent.value > 100) {
    notifyError('La promotion ne peut pas dépasser 100%.')
    return
  }
  
  isApplyingPromotion.value = true
  lastAppliedMessage.value = ''
  try {
    const vendeurId = authStore.user?.id_vendeur
    if (!vendeurId) throw new Error('Vendeur non connecté')
    
    const success = await applyGlobalPromotion(
      vendeurId, 
      globalPromotionPercent.value,
      applyToAlreadyDiscounted.value
    )
    if (success) {
      isGlobalPromotionActive.value = true
      lastAppliedMessage.value = `Promotion de ${globalPromotionPercent.value}% appliquée avec succès.`
      notifySuccess('Promotion appliquée avec succès à tous les produits.')
    } else {
      notifyError('Erreur lors de l\'application de la promotion.')
    }
  } catch (err) {
    notifyError('Erreur lors de l\'application de la promotion.')
  } finally {
    isApplyingPromotion.value = false
  }
}

const handleToggleGlobalPromotion = async () => {
  const newValue = !isGlobalPromotionActive.value
  
  if (newValue && (globalPromotionPercent.value === null || globalPromotionPercent.value <= 0)) {
    notifyError('Veuillez saisir un pourcentage avant d\'activer la promotion.')
    return
  }

  if (newValue && globalPromotionPercent.value > 100) {
    notifyError('La promotion ne peut pas dépasser 100%.')
    return
  }

  isApplyingPromotion.value = true
  lastAppliedMessage.value = ''
  try {
    const vendeurId = authStore.user?.id_vendeur
    if (!vendeurId) throw new Error('Vendeur non connecté')
    
    const percent = newValue ? (globalPromotionPercent.value || 0) : 0
    const success = await applyGlobalPromotion(
      vendeurId, 
      percent,
      newValue ? applyToAlreadyDiscounted.value : true // If deactivating, we reset everything anyway
    )
    
    if (success) {
      isGlobalPromotionActive.value = newValue
      if (newValue) {
        lastAppliedMessage.value = `Promotion de ${globalPromotionPercent.value}% activée.`
      } else {
        lastAppliedMessage.value = 'Promotion désactivée (prix réinitialisés).'
      }
      notifySuccess(newValue ? 'Promotion activée.' : 'Promotion désactivée.')
    } else {
      notifyError('Erreur lors du changement de statut de la promotion.')
    }
  } catch (err) {
    notifyError('Erreur lors du changement de statut de la promotion.')
  } finally {
    isApplyingPromotion.value = false
  }
}

const handleResetAllPromotions = () => {
  showResetModal.value = true
}

const confirmResetAllPromotions = async () => {
  showResetModal.value = false
  isApplyingPromotion.value = true
  lastAppliedMessage.value = ''
  try {
    const vendeurId = authStore.user?.id_vendeur
    if (!vendeurId) throw new Error('Vendeur non connecté')
    
    // Applying 0% is equivalent to resetting ALL promotions.
    const success = await applyGlobalPromotion(vendeurId, 0, true)
    
    if (success) {
      isGlobalPromotionActive.value = false
      globalPromotionPercent.value = 0
      lastAppliedMessage.value = 'Les promotions globales ont été supprimées (tous les prix ont été réinitialisés).'
      notifySuccess('Les promotions globales et individuelles ont été supprimées.')
    } else {
      notifyError('Erreur lors de la suppression des promotions.')
    }
  } catch (err) {
    notifyError('Erreur lors de la suppression des promotions.')
  } finally {
    isApplyingPromotion.value = false
  }
}

// ===== COMPOSABLES / LOGIQUE MÉTIER =====
const useDiscountStore = () => {
  const storeDiscounts = ref<Discount[]>([]) // Remises sur l'ensemble de la boutique
  const excludeDiscounts = ref<Discount[]>([]) // Remises excluant les nouveautés
  const isLoading = ref(false)
  const error = ref<string | null>(null)


  // Actions
  const fetchDiscounts = async () => {
    isLoading.value = true
    error.value = null
    
    try {
      const vendeurId = authStore.user?.id_vendeur
      
      if (!vendeurId) {
        error.value = 'Vendeur non connecté'
        return
      }
      
      const allRemises = await getRemises(vendeurId)
      
      // Fonction pour mapper les données
      const mapRemise = (remise: RemiseApi): Discount => ({
        id: remise.id_remise.toString(),
        libelle: remise.titre || 'Remise sans titre',
        montantMin: remise.montant_min || 0,
        montantMax: remise.montant_max || 0,
        dateDebut: remise.date_debut && remise.date_debut !== '0000-00-00 00:00:00' ? new Date(remise.date_debut).toLocaleDateString('fr-FR', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric' 
        }) : '',
        dateFin: remise.date_fin && remise.date_fin !== '0000-00-00 00:00:00' ? new Date(remise.date_fin).toLocaleDateString('fr-FR', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric' 
        }) : '',
        taux: remise.montant ?? 0,
        actif: remise.actif === 1 ? 'activé' : 'désactivé'
      })
      
      storeDiscounts.value = allRemises
        .filter(remise => remise.id_remise_type !== 6)
        .map(mapRemise)
      excludeDiscounts.value = allRemises
        .filter(remise => remise.id_remise_type === 6)
        .map(mapRemise)
        
    } catch (err) {
      error.value = 'Erreur lors du chargement des remises'
    } finally {
      isLoading.value = false
    }
  }

  const addDiscount = async (discountData: Omit<Discount, 'id'>, type: 'store' | 'exclude') => {
    const vendeurId = authStore.user?.id_vendeur
    
    if (!vendeurId) {
      throw new Error('Vendeur non connecté')
    }
    
    const input = {
      id_remise_type: type === 'store' ? 1 : 6, // Type 1: boutique, Type 6: excluant nouveautés
      id_vendeur: vendeurId,
      titre: discountData.libelle,
      montant_min: discountData.montantMin,
      montant_max: discountData.montantMax,
      montant: discountData.taux,
      date_debut: discountData.dateDebut,
      date_fin: discountData.dateFin,
      pourcentage: discountData.taux,
      type: 'pourcentage',
      actif: discountData.actif === 'activé' ? 1 : 0
    }
    
    const result = await createRemise(input)
    
    // Ajouter la nouvelle remise à la liste appropriée
    const newDiscount: Discount = {
      id: result.id_remise.toString(),
      libelle: result.titre || 'Remise sans titre',
      montantMin: result.montant_min || 0,
      montantMax: result.montant_max || 0,
      dateDebut: result.date_debut && result.date_debut !== '0000-00-00 00:00:00' ? new Date(result.date_debut).toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      }) : '',
      dateFin: result.date_fin && result.date_fin !== '0000-00-00 00:00:00' ? new Date(result.date_fin).toLocaleDateString('fr-FR', {
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      }) : '',
      taux: result.montant ?? 0,
      actif: result.actif === 1 ? 'activé' : 'désactivé'
    }
    
    // Ajouter à la bonne liste selon le type
    if (type === 'store') {
      storeDiscounts.value.push(newDiscount)
    } else {
      excludeDiscounts.value.push(newDiscount)
    }
    
    return newDiscount
  }

  const deleteDiscount = async (id: string) => {
    await deleteRemise(parseInt(id))
    
    // Supprimer de la liste locale appropriée
    storeDiscounts.value = storeDiscounts.value.filter(d => d.id !== id)
    excludeDiscounts.value = excludeDiscounts.value.filter(d => d.id !== id)
  }

  const toggleDiscountStatus = async (id: string) => {
    // Chercher dans les deux listes
    let discount = storeDiscounts.value.find(d => d.id === id)
    if (!discount) {
      discount = excludeDiscounts.value.find(d => d.id === id)
    }
    
    if (!discount) return
    
    const newStatus = discount.actif === 'activé' ? 0 : 1
    
    const input = {
      id_remise: parseInt(id),
      actif: newStatus
    }
    
    await updateRemise(input)
    
    // Mettre à jour le statut local
    discount.actif = newStatus === 1 ? 'activé' : 'désactivé'
  }

  return {
    // State
    storeDiscounts,
    excludeDiscounts,
    isLoading: readonly(isLoading),
    error,
    
    // Actions
    fetchDiscounts,
    addDiscount,
    deleteDiscount,
    toggleDiscountStatus
  }
}

// ===== COMPOSANT VUE =====
// Store
const {
  storeDiscounts,
  excludeDiscounts,
  isLoading,
  error,
  fetchDiscounts,
  addDiscount,
  deleteDiscount,
  toggleDiscountStatus
} = useDiscountStore()

// UI State
const showAddModal = ref(false)
const modalType = ref<'store' | 'exclude'>('store')
const showDeleteModal = ref(false)
const showResetModal = ref(false)
const discountToDelete = ref<Discount | null>(null)
const isSubmitting = ref(false)
const showToggleModal = ref(false)
const discountToToggle = ref<Discount | null>(null)

// Form data
const newDiscount = reactive({
  libelle: '',
  montantMin: '',
  montantMax: '',
  dateDebut: '',
  dateFin: '',
  taux: '',
  actif: 'activé'
})

// Computed
const modalTitle = computed(() => {
  return modalType.value === 'store' 
    ? 'Ajouter une remise sur l\'ensemble de la boutique'
    : 'Ajouter une remise excluant les nouveautés'
})

const toggleModalTitle = computed(() => {
  const isActive = discountToToggle.value?.actif === 'activé'
  return isActive ? 'Annuler la remise ?' : 'Appliquer la remise ?'
})

const toggleModalMessage = computed(() => {
  const name = discountToToggle.value?.libelle || 'cette remise'
  const isActive = discountToToggle.value?.actif === 'activé'
  return isActive
    ? `Voulez-vous annuler ${name} ?`
    : `Voulez-vous appliquer ${name} ?`
})

// Messages d'erreur
const validationErrors = ref({
  montantMin: '',
  montantMax: '',
  taux: '',
  dateRange: ''
})

// Fonction de validation pour le composant modal
const handleValidation = (errors: any) => {
  Object.assign(validationErrors.value, errors)
}

// Colonnes du tableau
const storeDiscountColumns = [
  { key: 'libelle', title: 'Libellé', sortable: true, align: 'left' as const },
  { key: 'montantMin', title: 'Montant minimum', sortable: true, align: 'left' as const },
  { key: 'montantMax', title: 'Montant maximum', sortable: true, align: 'left' as const },
  { key: 'dateDebut', title: 'Date Debut', sortable: true, align: 'left' as const },
  { key: 'dateFin', title: 'Date Fin', sortable: true, align: 'left' as const },
  { key: 'taux', title: 'Taux de remise', sortable: true, align: 'left' as const, formatter: (value: number) => value != null ? `${value}%` : '0%' }
]

// ===== MÉTHODES UI =====
const openAddDiscountModal = (type: 'store' | 'exclude') => {
  modalType.value = type
  showAddModal.value = true
  resetForm()
}

const resetForm = () => {
  newDiscount.libelle = ''
  newDiscount.montantMin = ''
  newDiscount.montantMax = ''
  newDiscount.dateDebut = ''
  newDiscount.dateFin = ''
  newDiscount.taux = ''
  newDiscount.actif = 'activé'
}

const handleAddDiscount = async (formData?: {
  libelle: string
  montantMin: string
  montantMax: string
  dateDebut: string
  dateFin: string
  taux: string
  actif: string
}) => {
  // Éviter les doubles clics si une soumission est déjà en cours
  if (isSubmitting.value) return
  // Utiliser les données envoyées par la modal si présentes，否则回退到本地状态
  const src = formData ?? newDiscount

  // Validation des champs requis (montantMin et montantMax sont optionnels)
  if (!src.libelle || !src.dateDebut || !src.dateFin || !src.taux) {
    // Ne pas utiliser l'état d'erreur global (réservé au chargement)
    notifyError('Veuillez remplir tous les champs obligatoires')
    return
  }

  // Validation des dates
  if (new Date(src.dateFin) <= new Date(src.dateDebut)) {
    notifyError('La date de fin doit être postérieure à la date de début')
    return
  }

  // Prévenir les doublons de libellé (insensible à la casse et aux espaces)
  const name = src.libelle.trim().toLowerCase()
  if (name) {
    const list = modalType.value === 'store' ? storeDiscounts.value : excludeDiscounts.value
    const exists = list.some(d => (d.libelle || '').trim().toLowerCase() === name)
    if (exists) {
      notifyError('Une remise avec ce libellé existe déjà.')
      return
    }
  }

  const discountData = {
    libelle: src.libelle.trim(),
    montantMin: src.montantMin ? parseFloat(src.montantMin) : 0,
    montantMax: src.montantMax ? parseFloat(src.montantMax) : 999999,
    dateDebut: src.dateDebut,
    dateFin: src.dateFin,
    // GraphQL attend un Int pour "pourcentage" -> forcer un entier
    taux: Number.parseInt(String(src.taux), 10),
    actif: (src.actif as 'activé' | 'désactivé') || 'activé'
  }

  isSubmitting.value = true
  try {
    await addDiscount(discountData, modalType.value)
    showAddModal.value = false
    resetForm()
    // Notification de succès (toast vert)
    notifySuccess('Remise ajoutée avec succès')
  } catch (e) {
    // laisser le message d'erreur géré plus haut si besoin
    notifyError('Échec de l\'ajout de la remise')
  } finally {
    isSubmitting.value = false
  }
}

const handleDeleteDiscount = (row: Discount) => {
  discountToDelete.value = row
  showDeleteModal.value = true
}

const confirmDelete = async () => {
  if (discountToDelete.value) {
    // Fermer la modal AVANT de lancer la requête pour éviter que le toast
    // apparaisse derrière la fenêtre de confirmation
    const id = discountToDelete.value.id
    showDeleteModal.value = false
    try {
      await deleteDiscount(id)
      discountToDelete.value = null
      // Notification de succès (toast vert)
      notifySuccess('Remise supprimée avec succès')
    } catch (err) {
      // La modal reste fermée; informer l'utilisateur via un toast rouge
      notifyError('Échec de la suppression de la remise')
    }
  }
}

const handleToggleDiscountStatus = async (row: Discount) => {
  // Ouvrir la modal de confirmation au lieu de basculer directement
  discountToToggle.value = row
  showToggleModal.value = true
}

const confirmToggle = async () => {
  if (!discountToToggle.value) return
  const id = discountToToggle.value.id
  const wasActive = discountToToggle.value.actif === 'activé'
  try {
    await toggleDiscountStatus(id)
    showToggleModal.value = false
    // Message utilisateur
    if (wasActive) {
      notifySuccess('Remise annulée')
    } else {
      notifySuccess('Remise appliquée')
    }
  } catch (err) {
    notifyError('Erreur lors du changement de statut')
  }
}


// Lifecycle
onMounted(async () => {
  if (!authStore.isInitialized) {
    await authStore.checkAuth()
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: 'auth' })
    return
  }
  fetchDiscounts()
  checkInitialGlobalPromotion()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(PageHeader), {
      title: "Mes remises et promotion",
      class: "mb-4"
    }),
    _createElementVNode("div", _hoisted_1, [
      _createVNode(DiscountSection, {
        discounts: _unref(storeDiscounts),
        "is-loading": _unref(isLoading),
        error: _unref(error),
        "section-title": "Remise sur l'ensemble de la boutique",
        "button-label": "Ajouter une remise",
        "empty-message": "Vous n'avez aucune remise.",
        columns: storeDiscountColumns,
        onAdd: _cache[0] || (_cache[0] = ($event: any) => (openAddDiscountModal('store'))),
        onRetry: _unref(fetchDiscounts),
        onDelete: handleDeleteDiscount,
        onToggleStatus: handleToggleDiscountStatus
      }, null, 8, ["discounts", "is-loading", "error", "onRetry"]),
      _createVNode(DiscountSection, {
        discounts: _unref(excludeDiscounts),
        "is-loading": false,
        error: null,
        "section-title": "Remise excluant les nouveautés",
        "button-label": "Ajouter une remise",
        "empty-message": "vous n'avez aucune remise.",
        columns: storeDiscountColumns,
        onAdd: _cache[1] || (_cache[1] = ($event: any) => (openAddDiscountModal('exclude'))),
        onRetry: _unref(fetchDiscounts),
        onDelete: handleDeleteDiscount,
        onToggleStatus: handleToggleDiscountStatus
      }, null, 8, ["discounts", "onRetry"]),
      _createElementVNode("div", _hoisted_2, [
        _cache[18] || (_cache[18] = _createElementVNode("div", { class: "bg-gray-50 px-6 py-4 border-b border-gray-200" }, [
          _createElementVNode("h3", { class: "text-lg font-medium text-black" }, "Appliquer une promotion globale aux produits")
        ], -1)),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _cache[12] || (_cache[12] = _createElementVNode("label", { class: "text-black font-medium whitespace-nowrap" }, "Pourcentage de promotion :", -1)),
                _createElementVNode("div", _hoisted_7, [
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((globalPromotionPercent).value = $event)),
                    type: "number",
                    min: "0",
                    max: "100",
                    class: "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8",
                    placeholder: "0",
                    disabled: isApplyingPromotion.value
                  }, null, 8, _hoisted_8), [
                    [_vModelText, globalPromotionPercent.value]
                  ]),
                  _cache[11] || (_cache[11] = _createElementVNode("span", { class: "absolute right-3 top-1/2 -translate-y-1/2 text-black" }, "%", -1))
                ])
              ]),
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("div", _hoisted_10, [
                  _withDirectives(_createElementVNode("input", {
                    id: "applyToDiscounted",
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((applyToAlreadyDiscounted).value = $event)),
                    type: "checkbox",
                    class: "w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer",
                    disabled: isApplyingPromotion.value
                  }, null, 8, _hoisted_11), [
                    [_vModelCheckbox, applyToAlreadyDiscounted.value]
                  ])
                ]),
                _cache[13] || (_cache[13] = _createElementVNode("label", {
                  for: "applyToDiscounted",
                  class: "text-sm text-black cursor-pointer select-none"
                }, " Appliquer la promotion aussi aux produits déjà remisés ", -1))
              ]),
              _createElementVNode("div", _hoisted_12, [
                (!isGlobalPromotionActive.value)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      onClick: applyGlobalPromotionAction,
                      class: "inline-flex items-center px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-all shadow-sm disabled:opacity-50",
                      disabled: isApplyingPromotion.value || !globalPromotionPercent.value || globalPromotionPercent.value <= 0
                    }, [
                      (isApplyingPromotion.value)
                        ? (_openBlock(), _createElementBlock("i", _hoisted_14))
                        : (_openBlock(), _createElementBlock("i", _hoisted_15)),
                      _cache[14] || (_cache[14] = _createTextVNode(" Appliquer la promotion "))
                    ], 8, _hoisted_13))
                  : _createCommentVNode("", true),
                _createElementVNode("button", {
                  onClick: handleResetAllPromotions,
                  class: "inline-flex items-center px-6 py-2.5 bg-white text-red-600 border border-red-200 font-medium rounded-lg hover:bg-red-50 transition-all shadow-sm disabled:opacity-50",
                  disabled: isApplyingPromotion.value
                }, [
                  (isApplyingPromotion.value)
                    ? (_openBlock(), _createElementBlock("i", _hoisted_17))
                    : (_openBlock(), _createElementBlock("i", _hoisted_18)),
                  _cache[15] || (_cache[15] = _createTextVNode(" Supprimer la promotion globale "))
                ], 8, _hoisted_16)
              ]),
              _createVNode(_Transition, { name: "fade" }, {
                default: _withCtx(() => [
                  (lastAppliedMessage.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                        _cache[16] || (_cache[16] = _createElementVNode("i", { class: "fas fa-info-circle" }, null, -1)),
                        _createElementVNode("span", _hoisted_20, _toDisplayString(lastAppliedMessage.value), 1)
                      ]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ]),
            _cache[17] || (_cache[17] = _createStaticVNode("<div class=\"bg-blue-50 rounded-lg p-5 border border-blue-100 h-fit\" data-v-72a79f57><div class=\"flex gap-3 text-blue-800 mb-2\" data-v-72a79f57><i class=\"fas fa-lightbulb mt-1\" data-v-72a79f57></i><span class=\"font-bold uppercase text-xs tracking-wider\" data-v-72a79f57>Note Importante</span></div><p class=\"text-sm text-blue-700 leading-relaxed\" data-v-72a79f57> Cette promotion globale met à jour directement le <strong data-v-72a79f57>prix réduit</strong> de vos produits. Les &quot;remises et promotion&quot; sont visibles dans le catalogue pour vos clients. </p></div>", 1))
          ])
        ])
      ]),
      _createVNode(AddDiscountModal, {
        "is-visible": showAddModal.value,
        title: modalTitle.value,
        "discount-data": newDiscount,
        "validation-errors": validationErrors.value,
        submitting: isSubmitting.value,
        "onUpdate:isVisible": _cache[4] || (_cache[4] = ($event: any) => (showAddModal.value = $event)),
        onSubmit: handleAddDiscount,
        onValidate: handleValidation
      }, null, 8, ["is-visible", "title", "discount-data", "validation-errors", "submitting"]),
      _createVNode(_unref(ModalComp), {
        "is-visible": showDeleteModal.value,
        title: "Confirmer la suppression",
        size: "md",
        "onUpdate:isVisible": _cache[6] || (_cache[6] = ($event: any) => (showDeleteModal.value = false))
      }, {
        body: _withCtx(() => [
          _createElementVNode("div", _hoisted_21, [
            _cache[21] || (_cache[21] = _createElementVNode("div", { class: "mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4" }, [
              _createElementVNode("i", { class: "fas fa-exclamation-triangle text-red-600 text-xl" })
            ], -1)),
            _cache[22] || (_cache[22] = _createElementVNode("h3", { class: "text-lg font-medium text-black mb-2" }, " Supprimer la remise ", -1)),
            _createElementVNode("p", _hoisted_22, [
              _cache[19] || (_cache[19] = _createTextVNode(" Êtes-vous sûr de vouloir supprimer la remise ")),
              _createElementVNode("strong", null, "\"" + _toDisplayString(discountToDelete.value?.libelle) + "\"", 1),
              _cache[20] || (_cache[20] = _createTextVNode(" ? "))
            ]),
            _cache[23] || (_cache[23] = _createElementVNode("p", { class: "text-xs text-black" }, " Cette action est irréversible. ", -1))
          ])
        ]),
        footer: _withCtx(() => [
          _createElementVNode("div", _hoisted_23, [
            _createElementVNode("button", {
              onClick: _cache[5] || (_cache[5] = ($event: any) => (showDeleteModal.value = false)),
              type: "button",
              class: "px-4 py-2 text-black bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none transition-colors"
            }, " Annuler "),
            _createElementVNode("button", {
              onClick: confirmDelete,
              type: "button",
              class: "px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none transition-colors"
            }, " Supprimer ")
          ])
        ]),
        _: 1
      }, 8, ["is-visible"]),
      _createVNode(_unref(ModalComp), {
        "is-visible": showResetModal.value,
        title: "Supprimer toutes les promotions ?",
        size: "md",
        "onUpdate:isVisible": _cache[8] || (_cache[8] = ($event: any) => (showResetModal.value = false))
      }, {
        default: _withCtx(() => _cache[24] || (_cache[24] = [
          _createElementVNode("div", { class: "text-center p-4" }, [
            _createElementVNode("div", { class: "mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4" }, [
              _createElementVNode("i", { class: "fas fa-trash-alt text-red-600 text-xl" })
            ]),
            _createElementVNode("h3", { class: "text-lg font-medium text-black mb-2" }, " Supprimer les promotions "),
            _createElementVNode("p", { class: "text-sm text-black mb-4" }, [
              _createTextVNode(" Êtes-vous sûr de vouloir supprimer "),
              _createElementVNode("strong", null, "toutes"),
              _createTextVNode(" les promotions (globales et individuelles) appliquées à vos produits ? ")
            ]),
            _createElementVNode("p", { class: "text-xs text-black italic" }, " Cette action réinitialisera les prix de tous vos produits à leur prix d'origine. ")
          ], -1)
        ])),
        footer: _withCtx(() => [
          _createElementVNode("div", _hoisted_24, [
            _createElementVNode("button", {
              onClick: _cache[7] || (_cache[7] = ($event: any) => (showResetModal.value = false)),
              type: "button",
              class: "px-4 py-2 text-black bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none transition-colors"
            }, " Annuler "),
            _createElementVNode("button", {
              onClick: confirmResetAllPromotions,
              type: "button",
              class: "px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none transition-colors"
            }, " Confirmer ")
          ])
        ]),
        _: 1
      }, 8, ["is-visible"]),
      _createVNode(_unref(ModalComp), {
        "is-visible": showToggleModal.value,
        title: toggleModalTitle.value,
        size: "md",
        "onUpdate:isVisible": _cache[10] || (_cache[10] = ($event: any) => (showToggleModal.value = false))
      }, {
        body: _withCtx(() => [
          _createElementVNode("div", _hoisted_25, [
            _cache[25] || (_cache[25] = _createElementVNode("div", { class: "mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4" }, [
              _createElementVNode("i", { class: "fas fa-check text-blue-600 text-xl" })
            ], -1)),
            _createElementVNode("h3", _hoisted_26, _toDisplayString(toggleModalTitle.value), 1),
            _createElementVNode("p", _hoisted_27, _toDisplayString(toggleModalMessage.value), 1)
          ])
        ]),
        footer: _withCtx(() => [
          _createElementVNode("div", _hoisted_28, [
            _createElementVNode("button", {
              onClick: _cache[9] || (_cache[9] = ($event: any) => (showToggleModal.value = false)),
              type: "button",
              class: "px-4 py-2 text-black bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none transition-colors"
            }, " Annuler "),
            _createElementVNode("button", {
              onClick: confirmToggle,
              type: "button",
              class: _normalizeClass(["px-4 py-2 text-white rounded-md focus:outline-none transition-colors", discountToToggle.value?.actif === 'activé' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'])
            }, _toDisplayString(discountToToggle.value?.actif === 'activé' ? 'Désactiver' : 'Appliquer'), 3)
          ])
        ]),
        _: 1
      }, 8, ["is-visible", "title"])
    ])
  ], 64))
}
}

})